// A list grid with the same toolbar everywhere: free-text search, select
// filters (status, kind, ...), pagination and checkbox selection with bulk
// actions. Search and filters live in the URL (?q=&status=), so a filtered
// view can be shared or reloaded. Bulk actions call the normal API once per
// selected row, one at a time (each still runs its own checks, e.g. the
// licence gate), and report every failure with its reason.
import type { ColDef, GridApi, IRowNode } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { gridTheme } from './grid'

export interface GridFilter<T> {
  /** URL parameter and label. */
  key: string
  label: string
  options: { value: string; label: string }[]
  value: (row: T) => string
}

export interface BulkAction<T> {
  label: string
  run: (row: T) => Promise<unknown>
  /** Rows the action applies to; the others are skipped (and counted). */
  applies?: (row: T) => boolean
  primary?: boolean
  /** Asked before running, with the number of rows. */
  confirm?: string
}

interface Props<T> {
  rows: T[] | undefined
  columnDefs: ColDef<T>[]
  getRowId: (row: T) => string
  loading?: boolean
  rowHeight?: number
  /** What free-text search looks in. */
  searchText: (row: T) => string
  filters?: GridFilter<T>[]
  actions?: BulkAction<T>[]
  /** Called after a bulk action (refetch). */
  onDone?: () => void
  onOpen?: (row: T) => void
  pageSize?: number
  /** URL parameter prefix when a page has two grids. */
  prefix?: string
}

interface Outcome {
  label: string
  ok: number
  skipped: number
  failed: { id: string; reason: string }[]
}

export function DataGrid<T>(p: Props<T>) {
  const [params, setParams] = useSearchParams()
  const k = (name: string) => (p.prefix ? `${p.prefix}_${name}` : name)
  const query = params.get(k('q')) ?? ''
  const filterValues = useMemo(() => Object.fromEntries((p.filters ?? []).map((f) => [f.key, params.get(k(f.key)) ?? ''])), [params, p.filters]) // eslint-disable-line react-hooks/exhaustive-deps
  const set = (name: string, v: string) =>
    setParams(
      (cur) => {
        const next = new URLSearchParams(cur)
        if (v) next.set(k(name), v)
        else next.delete(k(name))
        return next
      },
      { replace: true },
    )

  const gridApi = useRef<GridApi<T> | null>(null)
  const [selected, setSelected] = useState<T[]>([])
  const [shown, setShown] = useState(0)
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  // The filter reads the latest values through a ref; AG Grid keeps the
  // callbacks it was given.
  const state = useRef({ query, filterValues })
  const present = useCallback(() => state.current.query.trim() !== '' || Object.values(state.current.filterValues).some(Boolean), [])
  const passes = useCallback(
    (node: IRowNode<T>) => {
      const row = node.data
      if (!row) return false
      const words = state.current.query.toLowerCase().split(/\s+/).filter(Boolean)
      const hay = p.searchText(row).toLowerCase()
      if (!words.every((w) => hay.includes(w))) return false
      return (p.filters ?? []).every((f) => !state.current.filterValues[f.key] || f.value(row) === state.current.filterValues[f.key])
    },
    [p],
  )
  useEffect(() => {
    state.current = { query, filterValues }
    gridApi.current?.onFilterChanged()
  }, [query, filterValues, p.rows])

  async function runAction(a: BulkAction<T>) {
    const rows = selected
    const targets = rows.filter((r) => !a.applies || a.applies(r))
    if (targets.length === 0) {
      setOutcome({ label: a.label, ok: 0, skipped: rows.length, failed: [] })
      return
    }
    if (a.confirm && !window.confirm(`${a.confirm} (${targets.length})`)) return
    const out: Outcome = { label: a.label, ok: 0, skipped: rows.length - targets.length, failed: [] }
    setOutcome(null)
    for (let i = 0; i < targets.length; i++) {
      setBusy({ label: a.label, done: i, total: targets.length })
      try {
        await a.run(targets[i])
        out.ok++
      } catch (e) {
        out.failed.push({ id: p.getRowId(targets[i]), reason: e instanceof Error ? e.message : String(e) })
      }
    }
    setBusy(null)
    setOutcome(out)
    gridApi.current?.deselectAll()
    p.onDone?.()
  }

  const total = p.rows?.length ?? 0
  return (
    <div className="dg">
      <div className="dg-bar">
        <input
          className="dg-search"
          type="search"
          placeholder="Search…"
          aria-label="Search"
          value={query}
          onChange={(e) => set('q', e.target.value)}
        />
        {(p.filters ?? []).map((f) => (
          <label key={f.key} className="dg-filter">
            <span>{f.label}</span>
            <select value={filterValues[f.key]} onChange={(e) => set(f.key, e.target.value)}>
              <option value="">All</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        {(query || Object.values(filterValues).some(Boolean)) && (
          <button
            className="btn btn-sm"
            onClick={() =>
              setParams(
                (cur) => {
                  const next = new URLSearchParams(cur)
                  for (const n of ['q', ...(p.filters ?? []).map((f) => f.key)]) next.delete(k(n))
                  return next
                },
                { replace: true },
              )
            }
          >
            Clear
          </button>
        )}
        <span className="muted dg-count">
          {shown === total ? `${total} rows` : `${shown} of ${total}`}
        </span>
      </div>
      {p.actions && p.actions.length > 0 && selected.length > 0 && (
        <div className="dg-actions">
          <b>{selected.length} selected</b>
          {p.actions.map((a) => (
            <button key={a.label} className={'btn btn-sm' + (a.primary ? ' btn-primary' : '')} disabled={!!busy} onClick={() => runAction(a)}>
              {a.label}
            </button>
          ))}
          <button className="btn btn-sm" disabled={!!busy} onClick={() => gridApi.current?.deselectAll()}>
            Clear selection
          </button>
        </div>
      )}
      {busy && (
        <p className="callout" role="status">
          {busy.label}: {busy.done + 1} of {busy.total}…
        </p>
      )}
      {outcome && (
        <div className={'callout' + (outcome.failed.length ? ' err' : '')} role="status">
          <span>
            {outcome.label}: {outcome.ok} done
            {outcome.skipped > 0 && `, ${outcome.skipped} skipped (not applicable)`}
            {outcome.failed.length > 0 && `, ${outcome.failed.length} failed`}.
          </span>{' '}
          <button className="btn btn-sm" onClick={() => setOutcome(null)}>
            Dismiss
          </button>
          {outcome.failed.length > 0 && (
            <ul className="dg-fail">
              {outcome.failed.map((f) => (
                <li key={f.id}>
                  <b className="mono">{f.id}</b>: {f.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="grid-wrap">
        <AgGridReact<T>
          theme={gridTheme}
          rowData={p.rows}
          columnDefs={p.columnDefs}
          rowHeight={p.rowHeight ?? 52}
          loading={p.loading}
          domLayout="autoHeight"
          getRowId={(r) => p.getRowId(r.data)}
          pagination
          paginationPageSize={p.pageSize ?? 25}
          paginationPageSizeSelector={[10, 25, 50, 100]}
          rowSelection={p.actions?.length ? { mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false } : undefined}
          isExternalFilterPresent={present}
          doesExternalFilterPass={passes}
          onGridReady={(e) => {
            gridApi.current = e.api
            setShown(e.api.getDisplayedRowCount())
          }}
          onModelUpdated={(e) => setShown(e.api.getDisplayedRowCount())}
          onSelectionChanged={(e) => setSelected(e.api.getSelectedRows())}
          onRowDoubleClicked={(e) => e.data && p.onOpen?.(e.data)}
          tooltipShowDelay={300}
          defaultColDef={{ sortable: true, resizable: true }}
        />
      </div>
    </div>
  )
}
